import { useTranslation } from 'react-i18next'
import { motion } from 'motion/react'
import { useContextPanels } from '../../contexts/panelsContext'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import routePaths from '../../constants/routePaths'
import { UIElement } from '../../types/UIElements'
import { cn } from '../../utils/cn'
import useOpenFilter from '../FilterBar/useOpenFilter'
import { buttonResetClassName, separatorClassName } from './classNames'
import { contentVariants } from './menuAnimation'

const SideMenuGroup = (props: {
  title: string
  items: { label: string; onClick: () => void }[]
}) => (
  <section>
    <motion.h2
      className="m-0 mb-5 text-[13px] tracking-[1.34px] sm:text-[14px] md:mb-[26px] md:text-[1.6em] xl:mb-8 xxl:text-[16px] short-sm:mb-2 short-sm:text-[16px]"
      variants={contentVariants}
    >
      {props.title}
    </motion.h2>
    <ul className="m-0 flex list-none flex-col items-start gap-[18px] p-0 md:gap-6 xl:gap-7 short-sm:gap-0">
      {props.items.map(item => (
        <motion.li key={item.label} variants={contentVariants}>
          <button
            type="button"
            className={cn(
              buttonResetClassName,
              'text-[17px] uppercase tracking-[1.34px] sm:text-[19px] sm:tracking-[1.44px] md:text-[2.2em] lg:text-[2.4em] lg:tracking-[1.54px] xxl:text-[22px] short-sm:py-3 short-sm:text-[22px]',
            )}
            onClick={item.onClick}
          >
            {item.label}
          </button>
        </motion.li>
      ))}
    </ul>
  </section>
)

// The filters and the about pages, stacked on larger screens and side by side on landscape phones
const SideMenuNav = () => {
  const { toggleExplanationFromMenu, openSheet } = useContextPanels()
  const { setDetailsOpen } = useContextMotifDetails()
  const { t } = useTranslation()
  const { openFilter } = useOpenFilter()

  const toggleHowWorks = () => {
    setDetailsOpen(false)
    window.history.replaceState(null, '', routePaths.EXPLANATION)
    toggleExplanationFromMenu()
  }

  const handleOpenAboutProject = () => {
    setDetailsOpen(false)
    window.history.replaceState(null, '', routePaths.ABOUT)
    openSheet(UIElement.ABOUT)
  }

  const handleOpenGallery = () => {
    setDetailsOpen(false)
    window.history.replaceState(null, '', routePaths.GALLERY)
    openSheet(UIElement.GALLERY)
  }

  const filterItems = [
    { label: t('sideMenu.location'), onClick: () => openFilter(UIElement.GEOFILTER) },
    { label: t('sideMenu.theme'), onClick: () => openFilter(UIElement.TAGFILTER) },
    { label: t('sideMenu.tag'), onClick: () => openFilter(UIElement.KEYWORDFILTER) },
  ]

  const aboutItems = [
    { label: t('sideMenu.howItWorks'), onClick: toggleHowWorks },
    { label: t('sideMenu.ourStory'), onClick: handleOpenAboutProject },
    { label: t('sideMenu.gallery'), onClick: handleOpenGallery },
  ]

  return (
    <nav className="mb-5 mt-10 flex flex-col md:mt-10 xl:mt-16 short-sm:my-auto short-sm:flex-row short-sm:justify-center">
      <SideMenuGroup title={t('sideMenu.filters')} items={filterItems} />
      <motion.hr
        className={cn(
          separatorClassName,
          'my-8 md:my-11 xl:my-[52px] short-sm:mx-16 short-sm:my-0 short-sm:h-auto short-sm:w-px short-sm:self-stretch',
        )}
        variants={contentVariants}
      />
      <SideMenuGroup title={t('sideMenu.about')} items={aboutItems} />
    </nav>
  )
}

export default SideMenuNav
